import { useMemo, useRef, useState } from 'react';
import {
  Badge,
  Card,
  EmptyState,
  ErrorMessage,
  Field,
  Loading,
  Modal,
  ModuleHeader,
  Toggle,
  useAsync,
  useToast,
} from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  IconBell,
  IconCode,
  IconEdit,
  IconEye,
  IconFile,
  IconMail,
  IconPlus,
  IconSearch,
  IconTrash,
} from '../../components/icons';

const CHANNEL_LABELS: Record<string, string> = { email: 'E-Mail', push: 'Push', sms: 'SMS' };

/**
 * Verwaltung der Vorlagen mit Platzhalter-Referenz (Kap. 9 und 17).
 *
 * Die Referenz stammt aus dem Server (`/templates/placeholders`) und damit
 * aus derselben Quelle wie die Ersetzung – eine im Frontend gepflegte Liste
 * würde über kurz oder lang abweichen.
 */
export function TemplateSettings() {
  const { can } = useAuth();
  const { notify } = useToast();
  const canManage = can('admin.catalogs.manage');
  const templates = useAsync(() => api.get('/api/catalogs/templates'), []);
  const placeholders = useAsync(() => api.get('/api/catalogs/templates/placeholders').catch(() => []), []);

  const [editing, setEditing] = useState<any | null>(null);
  const [showReference, setShowReference] = useState(false);

  const remove = async (template: any) => {
    if (!window.confirm(`Vorlage „${template.name}“ wirklich löschen?`)) return;
    try {
      await api.delete(`/api/catalogs/templates/${template.key}`);
      notify('Vorlage gelöscht.', 'success');
      templates.reload();
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : 'Löschen fehlgeschlagen.', 'error');
    }
  };

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconBell width={22} height={22} />}
        title="Push-Mitteilungen"
        subtitle="Textbausteine und Vorlagen für E-Mail, Push und SMS – Platzhalter werden beim Versand ersetzt."
        pill="Konfiguration"
        tone="primary"
      />

      <Card
        title="Textbausteine & Vorlagen"
        subtitle="Platzhalter in doppelten geschweiften Klammern werden beim Versand ersetzt"
        actions={
          <>
            <button className="btn sm" onClick={() => setShowReference(true)}>
              <IconCode width={14} height={14} /> Platzhalter-Referenz
            </button>
            {canManage && (
              <button
                className="btn sm primary"
                onClick={() =>
                  setEditing({ key: '', name: '', subject: '', body: '', channel: 'email', is_active: 1, isNew: true })
                }
              >
                <IconPlus width={14} height={14} /> Neue Vorlage
              </button>
            )}
          </>
        }
        noBody
      >
        {templates.loading ? (
          <Loading />
        ) : !templates.data?.length ? (
          <EmptyState
            icon={<IconFile width={20} height={20} />}
            title="Noch keine Vorlage"
            hint="Legen Sie Textbausteine für Meldebestätigung, Terminankündigung oder Abschlussmeldung an."
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Vorlage</th>
                  <th>Kanal</th>
                  <th>Betreff</th>
                  <th>Inhalt</th>
                  <th>Status</th>
                  <th className="actions">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {templates.data.map((template: any) => (
                  <tr key={template.id}>
                    <td>
                      <strong>{template.name}</strong>
                      <div className="small subtle mono">{template.key}</div>
                    </td>
                    <td>
                      <Badge variant={template.channel === 'email' ? 'primary' : 'info'}>
                        {CHANNEL_LABELS[template.channel] ?? template.channel}
                      </Badge>
                    </td>
                    <td className="small">{template.subject ?? '–'}</td>
                    <td className="small subtle truncate" style={{ maxWidth: 320 }}>
                      {template.body}
                    </td>
                    <td>
                      <Badge variant={template.is_active ? 'success' : ''}>
                        {template.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </td>
                    <td className="actions">
                      <button className="btn sm" onClick={() => setEditing({ ...template })}>
                        <IconEdit width={13} height={13} /> {canManage ? 'Bearbeiten' : 'Ansehen'}
                      </button>
                      {canManage && (
                        <button
                          className="btn sm ghost"
                          onClick={() => remove(template)}
                          title="Vorlage löschen"
                          style={{ marginLeft: 6 }}
                        >
                          <IconTrash width={13} height={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <TemplateEditor
          template={editing}
          groups={placeholders.data ?? []}
          canManage={canManage}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            templates.reload();
            notify('Vorlage gespeichert.', 'success');
          }}
        />
      )}

      {showReference && (
        <PlaceholderReference groups={placeholders.data ?? []} onClose={() => setShowReference(false)} />
      )}
    </div>
  );
}

/** Bearbeiten und Anlegen einer Vorlage inklusive Vorschau. */
function TemplateEditor({
  template,
  groups,
  canManage,
  onClose,
  onSaved,
}: {
  template: any;
  groups: any[];
  canManage: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    key: template.key ?? '',
    name: template.name ?? '',
    subject: template.subject ?? '',
    body: template.body ?? '',
    channel: template.channel ?? 'email',
    isActive: template.is_active !== 0,
  });
  const [error, setError] = useState<unknown>(null);
  const [preview, setPreview] = useState<{ subject: string | null; body: string; unknown: string[] } | null>(null);
  const [busy, setBusy] = useState(false);

  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  // Der zuletzt berührte Textbereich bekommt den eingefügten Platzhalter.
  const lastFocused = useRef<'body' | 'subject'>('body');

  const set = (key: string, value: unknown) => setForm((current) => ({ ...current, [key]: value }));

  const insertPlaceholder = (key: string) => {
    const token = `{{${key}}}`;
    const target = lastFocused.current === 'subject' ? subjectRef.current : bodyRef.current;
    if (!target) return;

    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? start;
    const next = `${target.value.slice(0, start)}${token}${target.value.slice(end)}`;
    set(lastFocused.current === 'subject' ? 'subject' : 'body', next);

    // Schreibmarke hinter den eingefügten Platzhalter setzen.
    requestAnimationFrame(() => {
      target.focus();
      target.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      setPreview(await api.post('/api/catalogs/templates/preview', { subject: form.subject, body: form.body }));
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: form.name,
        subject: form.subject || null,
        body: form.body,
        channel: form.channel,
        isActive: form.isActive,
      };
      if (template.isNew) await api.post('/api/catalogs/templates', { ...payload, key: form.key });
      else await api.put(`/api/catalogs/templates/${template.key}`, payload);
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={template.isNew ? 'Neue Vorlage' : `Vorlage: ${template.name}`}
      size="xwide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={runPreview} disabled={busy || !form.body}>
            <IconEye width={14} height={14} /> Vorschau
          </button>
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          {canManage && (
            <button className="btn primary" onClick={save} disabled={busy || !form.name || !form.body || (template.isNew && !form.key)}>
              Speichern
            </button>
          )}
        </>
      }
    >
      <ErrorMessage error={error} />

      <div className="template-editor">
        <div className="stack">
          <div className="form-grid">
            <Field label="Bezeichnung" required>
              <input value={form.name} disabled={!canManage} onChange={(event) => set('name', event.target.value)} />
            </Field>
            <Field
              label="Schlüssel"
              required={template.isNew}
              hint={template.isNew ? 'Kleinbuchstaben, Ziffern, Punkt und Unterstrich.' : 'Nach dem Anlegen unveränderlich.'}
            >
              <input
                value={form.key}
                disabled={!template.isNew || !canManage}
                placeholder="task_reminder"
                onChange={(event) => set('key', event.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, ''))}
              />
            </Field>
            <Field label="Kanal">
              <select value={form.channel} disabled={!canManage} onChange={(event) => set('channel', event.target.value)}>
                <option value="email">E-Mail</option>
                <option value="push">Push-Benachrichtigung</option>
                <option value="sms">SMS</option>
              </select>
            </Field>
            <Field label="Status">
              <Toggle
                checked={form.isActive}
                disabled={!canManage}
                onChange={(value) => set('isActive', value)}
                label={form.isActive ? 'Aktiv' : 'Inaktiv'}
              />
            </Field>
          </div>

          <Field label="Betreff" hint="Bei Push und SMS als Titel verwendet.">
            <input
              ref={subjectRef}
              value={form.subject}
              disabled={!canManage}
              onFocus={() => (lastFocused.current = 'subject')}
              onChange={(event) => set('subject', event.target.value)}
            />
          </Field>

          <Field label="Inhalt" required>
            <textarea
              ref={bodyRef}
              value={form.body}
              disabled={!canManage}
              rows={12}
              style={{ minHeight: 220, fontFamily: 'var(--font-mono)', fontSize: 12.5 }}
              onFocus={() => (lastFocused.current = 'body')}
              onChange={(event) => set('body', event.target.value)}
            />
          </Field>

          {preview && (
            <div className="stack" style={{ gap: 8 }}>
              <span className="section-title">Vorschau mit Beispielwerten</span>
              {preview.unknown.length > 0 && (
                <div className="alert warning">
                  <IconCode width={16} height={16} />
                  <span>
                    Unbekannte Platzhalter bleiben beim Versand unverändert stehen:{' '}
                    <span className="mono">{preview.unknown.map((key) => `{{${key}}}`).join(', ')}</span>
                  </span>
                </div>
              )}
              <div className="preview-pane">
                {preview.subject && (
                  <div className="preview-subject">
                    <IconMail width={14} height={14} /> {preview.subject}
                  </div>
                )}
                <pre>{preview.body}</pre>
              </div>
            </div>
          )}
        </div>

        {/* Platzhalter-Palette: Klick fügt an der Schreibmarke ein. */}
        <aside className="placeholder-palette">
          <span className="section-title">Platzhalter einfügen</span>
          <p className="small subtle">
            Klicken Sie einen Eintrag an, um ihn an der Schreibmarke einzusetzen.
          </p>
          {groups.map((group: any) => (
            <div key={group.key} className="placeholder-group">
              <strong>{group.label}</strong>
              <div className="placeholder-chips">
                {group.placeholders.map((placeholder: any) => (
                  <button
                    key={placeholder.key}
                    type="button"
                    className="placeholder-chip"
                    disabled={!canManage}
                    title={`${placeholder.label} – ${placeholder.description}`}
                    onClick={() => insertPlaceholder(placeholder.key)}
                  >
                    {`{{${placeholder.key}}}`}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </aside>
      </div>
    </Modal>
  );
}

/** Vollständige Referenz aller Platzhalter mit Beschreibung und Beispiel. */
function PlaceholderReference({ groups, onClose }: { groups: any[]; onClose: () => void }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return groups;
    return groups
      .map((group: any) => ({
        ...group,
        placeholders: group.placeholders.filter((placeholder: any) =>
          `${placeholder.key} ${placeholder.label} ${placeholder.description}`.toLowerCase().includes(needle),
        ),
      }))
      .filter((group: any) => group.placeholders.length > 0);
  }, [groups, search]);

  return (
    <Modal
      title="Platzhalter-Referenz"
      size="wide"
      onClose={onClose}
      footer={
        <button className="btn primary" onClick={onClose}>
          Schließen
        </button>
      }
    >
      <div className="stack">
        <div className="alert info">
          <IconCode width={16} height={16} />
          <span>
            Platzhalter stehen in doppelten geschweiften Klammern, etwa <span className="mono">{'{{task_no}}'}</span>.
            Leerzeichen innerhalb der Klammern sind erlaubt. Namen, die hier nicht aufgeführt sind, bleiben beim
            Versand unverändert im Text stehen – so fällt ein Tippfehler auf, statt eine Lücke zu hinterlassen.
          </span>
        </div>

        <div style={{ position: 'relative' }}>
          <input
            type="search"
            placeholder="Platzhalter suchen …"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ paddingLeft: 34 }}
          />
          <span style={{ position: 'absolute', left: 11, top: 11, color: 'var(--text-subtle)' }}>
            <IconSearch width={15} height={15} />
          </span>
        </div>

        {filtered.length === 0 ? (
          <EmptyState title="Kein Treffer" hint="Kein Platzhalter passt zur Suche." />
        ) : (
          filtered.map((group: any) => (
            <div key={group.key} className="stack" style={{ gap: 6 }}>
              <span className="section-title">{group.label}</span>
              <p className="small subtle" style={{ margin: 0 }}>
                {group.description}
              </p>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th style={{ width: 190 }}>Platzhalter</th>
                      <th style={{ width: 160 }}>Bedeutung</th>
                      <th>Beschreibung</th>
                      <th style={{ width: 220 }}>Beispiel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.placeholders.map((placeholder: any) => (
                      <tr key={placeholder.key}>
                        <td className="mono">{`{{${placeholder.key}}}`}</td>
                        <td className="small">
                          <strong>{placeholder.label}</strong>
                        </td>
                        <td className="small subtle">{placeholder.description}</td>
                        <td className="small subtle">{placeholder.example}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}
